import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import type { PermissionDTO } from '@next-space/fe-api-idl';
import { PermissionRole, PermissionType } from '@next-space/fe-api-idl';
import type { FC } from 'react';
import { Icon } from 'src/common/components/icon';
import { PUBLIC_SHARE } from 'src/common/const';

import { CurrentSpaceSubject, GroupSubject, UserSubject } from '../subject';

export const PermissionChange: FC<{
  from?: PermissionDTO;
  to?: PermissionDTO;
}> = ({ from, to }) => {
  const permission = to ?? from;
  if (permission == null) {
    return null;
  }

  const changeLine = (() => {
    if (from !== undefined && to !== undefined) {
      return (
        <div className="flex items-center text-t2">
          <span>
            <RenderPermissionRole permission={from} />
          </span>
          <Icon name="IcDateEnd" size="middle" className="mx-2 text-grey3" />
          <span>
            <RenderPermissionRole permission={to} />
          </span>
        </div>
      );
    }
    if (from !== undefined && to === undefined) {
      return (
        <div className="text-t2">
          <span className="line-through text-grey4">
            <RenderPermissionRole permission={from} />
          </span>
        </div>
      );
    }
    if (to !== undefined && from === undefined) {
      return (
        <div className="text-t2">
          <span className="bg-active_color/10 text-active_color">
            <RenderPermissionRole permission={to} />
          </span>
        </div>
      );
    }
    return null;
  })();

  return (
    <div className="mt-2 px-1">
      <div className="text-t4 text-grey3">
        <RenderPermissionTarget permission={permission} />
      </div>
      {changeLine}
    </div>
  );
};

const RenderPermissionRole: FC<{ permission: PermissionDTO }> = ({ permission }) => {
  const { t } = useAppTranslation();

  if (permission.type === PermissionType.ILLEGAL) return null;
  if (permission.type === PermissionType.PUBLIC) return <>{t('common.can_view')}</>;
  if (permission.type === PermissionType.RESTRICTED) return <>{t('common.not_enabled')}</>;
  switch (permission.role) {
    case PermissionRole.NONE:
      return <>{t('common.no_access_permission')}</>;
    case PermissionRole.READER:
      return <>{t('components.page_feeds.permission.read_only')}</>;
    case PermissionRole.COMMENTER:
      return <>{t('common.can_comment')}</>;
    case PermissionRole.WRITER:
      return <>{t('common.can_edit')}</>;
    case PermissionRole.EDITOR:
      return <>{t('common.full_access')}</>;
    default:
      return null;
  }
};

const RenderPermissionTarget: FC<{ permission: PermissionDTO }> = ({ permission }) => {
  const { t } = useAppTranslation();

  switch (permission.type) {
    case PermissionType.PUBLIC:
    case PermissionType.RESTRICTED:
      return <>{PUBLIC_SHARE}</>;
    case PermissionType.ILLEGAL:
      return null;
    case PermissionType.SPACE:
      return <CurrentSpaceSubject />;
    case PermissionType.GROUP:
      return (
        <>
          {t('components.page_feeds.permission.group_prefix')}
          <GroupSubject groupId={permission.groupId} />
          {t('components.page_feeds.permission.group_suffix')}
        </>
      );
    case PermissionType.USER:
      return <UserSubject userId={permission.userId} />;
    default:
      return null;
  }
};
